import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Car, Package, ShieldCheck, Plus, Pencil, Trash2, Loader2, AlertTriangle, Clock, Search, ChevronRight, CheckCircle2 } from "lucide-react";

const ACCENT = "#3b5b8c";

const META = {
  vehicles: {
    label: "Vehicle Register", singular: "vehicle", icon: Car,
    desc: "Fleet vehicles with registration, insurance and service expiry tracking.",
    statuses: ["Active", "In Service", "Under Repair", "Off Road", "Sold", "Retired"],
    cols: [["rego", "Rego"], ["make_model", "Make & model"], ["year", "Year"], ["assigned_to", "Assigned to"], ["status", "Status"]],
    fields: [["rego", "Rego / plate"], ["make_model", "Make & model"], ["year", "Year"], ["vin", "VIN"],
      ["registration_expiry", "Registration expiry", "date"], ["insurer", "Insurer"], ["insurance_expiry", "Comprehensive insurance expiry", "date"],
      ["service_due", "Service due", "date"], ["assigned_to", "Assigned to"], ["status", "Status", "status"], ["notes", "Notes", "area"]],
  },
  assets: {
    label: "Asset Register", singular: "asset", icon: Package,
    desc: "Equipment and assets with purchase, warranty and location details.",
    statuses: ["Active", "In Use", "In Storage", "Under Repair", "Retired", "Lost"],
    cols: [["name", "Name"], ["category", "Category"], ["serial", "Serial / tag"], ["location", "Location"], ["status", "Status"]],
    fields: [["name", "Name"], ["category", "Category"], ["serial", "Serial / tag"], ["purchase_date", "Purchase date", "date"],
      ["purchase_cost", "Purchase cost"], ["location", "Location"], ["assigned_to", "Assigned to"],
      ["warranty_expiry", "Warranty expiry", "date"], ["status", "Status", "status"], ["notes", "Notes", "area"]],
  },
  insurances: {
    label: "Insurance Register", singular: "policy", icon: ShieldCheck,
    desc: "Business insurance policies with renewal-date tracking.",
    cols: [["policy_type", "Policy"], ["insurer", "Insurer"], ["policy_number", "Policy #"], ["sum_insured", "Sum insured"]],
    fields: [["policy_type", "Policy type"], ["insurer", "Insurer"], ["policy_number", "Policy number"], ["sum_insured", "Sum insured"],
      ["start_date", "Start date", "date"], ["expiry_date", "Expiry / renewal date", "date"], ["premium", "Premium"],
      ["broker", "Broker / contact"], ["notes", "Notes", "area"]],
  },
};
const KINDS = ["vehicles", "assets", "insurances"];

const STATUS_TONE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Service": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Use": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Storage": "bg-sky-50 text-sky-700 border-sky-200",
  "Under Repair": "bg-amber-50 text-amber-700 border-amber-200",
  "Off Road": "bg-amber-50 text-amber-700 border-amber-200",
  Retired: "bg-slate-100 text-slate-500 border-slate-200",
  Sold: "bg-slate-100 text-slate-500 border-slate-200",
  Lost: "bg-rose-50 text-rose-700 border-rose-200",
};

const ExpBadge = ({ tone, children }) => {
  const c = tone === "expired" ? "bg-rose-50 text-rose-700 border-rose-200"
    : tone === "due" ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-500 border-slate-200";
  return <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${c}`}>{children}</span>;
};
const StatusBadge = ({ value }) => {
  if (!value) return <span className="text-slate-300">—</span>;
  const c = STATUS_TONE[value] || "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border ${c}`}>{value}</span>;
};
const expLabel = (st) => st.tone === "expired" ? `Expired ${-st.days}d ago` : st.tone === "due" ? `Due in ${st.days}d` : st.date;
const kindTitle = (k) => (META[k]?.singular ? META[k].singular[0].toUpperCase() + META[k].singular.slice(1) : k);

export default function Organisation() {
  const { kind } = useParams();
  return kind && KINDS.includes(kind) ? <Register kind={kind} /> : <Overview />;
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.orgSummary().then(setData).catch(() => setData({ counts: {}, upcoming: [] })); }, []);

  const stats = useMemo(() => {
    const m = {};
    (data?.upcoming || []).forEach((u) => {
      m[u.kind] = m[u.kind] || { due: 0, expired: 0 };
      if (u.tone === "expired") m[u.kind].expired += 1; else m[u.kind].due += 1;
    });
    return m;
  }, [data]);

  const tiles = [
    { kind: "vehicles", label: "Vehicles", icon: Car, to: "/organisation/vehicles" },
    { kind: "assets", label: "Assets", icon: Package, to: "/organisation/assets" },
    { kind: "insurances", label: "Insurance policies", icon: ShieldCheck, to: "/organisation/insurances" },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="org-overview">
      <div className="flex items-center gap-2.5">
        <Building2 className="h-5 w-5" style={{ color: ACCENT }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Organisation</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">Vehicle, asset and insurance registers with expiry tracking. A daily digest of upcoming expiries is emailed to HR.</p>

      <div className="grid sm:grid-cols-3 gap-4">
        {tiles.map((t) => {
          const s = stats[t.kind] || {};
          return (
            <Link key={t.kind} to={t.to} data-testid={`org-tile-${t.kind}`}
              className="group bg-white border border-slate-200 rounded-xl p-4 hover:border-[#3b5b8c] hover:shadow-sm transition-all">
              <div className="flex items-start justify-between">
                <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${ACCENT}14` }}>
                  <t.icon className="h-5 w-5" style={{ color: ACCENT }} />
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-[#3b5b8c] transition-colors" />
              </div>
              <div className="text-3xl font-bold text-slate-800 mt-3 font-brand tabular-nums">{data?.counts?.[t.kind] ?? "—"}</div>
              <div className="text-xs text-slate-500">{t.label}</div>
              <div className="flex flex-wrap gap-1 mt-2 min-h-[18px]">
                {s.expired > 0 && <ExpBadge tone="expired"><AlertTriangle className="h-3 w-3" />{s.expired} expired</ExpBadge>}
                {s.due > 0 && <ExpBadge tone="due"><Clock className="h-3 w-3" />{s.due} due soon</ExpBadge>}
                {!s.expired && !s.due && data && <ExpBadge tone="ok"><CheckCircle2 className="h-3 w-3" />All current</ExpBadge>}
              </div>
            </Link>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4" style={{ color: ACCENT }} />
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Upcoming &amp; overdue expiries</h2>
        </div>
        {!data ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : !data.upcoming.length ? (
          <div className="flex items-center gap-2 text-xs text-slate-400 py-4 justify-center border border-dashed border-slate-200 rounded-lg">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Nothing expiring in the next 30 days.
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="org-upcoming">
            {data.upcoming.map((u, i) => (
              <Link key={i} to={`/organisation/${u.kind}`} className="flex items-center gap-3 py-2.5 text-sm hover:bg-slate-50/70 -mx-1 px-1 rounded transition-colors">
                <span className="font-medium text-slate-700 truncate">{u.item}</span>
                <span className="text-[11px] text-slate-400 shrink-0">{kindTitle(u.kind)} · {u.label}</span>
                <span className="ml-auto shrink-0">
                  <ExpBadge tone={u.tone}>{u.tone === "expired" ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{expLabel(u)}</ExpBadge>
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Register({ kind }) {
  const meta = META[kind];
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {} for new, record for edit
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.orgList(kind).then((r) => setItems(r.items)).catch(() => setItems([]));
  useEffect(() => { setItems(null); setQ(""); load(); }, [kind]);

  const open = (rec) => { setForm(rec ? { ...rec } : {}); setEditing(rec || {}); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.orgUpdate(kind, editing.id, form);
      else await api.orgCreate(kind, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const remove = async (rec) => {
    if (!window.confirm(`Delete this ${meta.singular} record?`)) return;
    try { await api.orgDelete(kind, rec.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const filtered = useMemo(() => {
    if (!items) return null;
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((r) => meta.cols.some(([k]) => String(r[k] ?? "").toLowerCase().includes(t)));
  }, [items, q, meta.cols]);

  const summary = useMemo(() => {
    const s = { total: items?.length || 0, due: 0, expired: 0 };
    (items || []).forEach((r) => {
      Object.values(r._expiries || {}).forEach((st) => {
        if (st.tone === "expired") s.expired += 1; else if (st.tone === "due") s.due += 1;
      });
    });
    return s;
  }, [items]);

  const Icon = meta.icon;
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid={`org-register-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <Icon className="h-5 w-5" style={{ color: ACCENT }} />
            <h1 className="text-xl font-semibold font-brand text-slate-800">{meta.label}</h1>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{meta.desc}</p>
        </div>
        <Button size="sm" className="h-9 text-xs shrink-0" style={{ backgroundColor: ACCENT }} onClick={() => open(null)} data-testid="org-add">
          <Plus className="h-4 w-4 mr-1" /> Add {meta.singular}
        </Button>
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${meta.label.toLowerCase()}…`} className="h-9 pl-9 text-sm bg-white" data-testid="org-search" />
        </div>
        <span className="text-[11px] text-slate-400">{summary.total} record{summary.total === 1 ? "" : "s"}</span>
        {summary.expired > 0 && <ExpBadge tone="expired"><AlertTriangle className="h-3 w-3" />{summary.expired} expired</ExpBadge>}
        {summary.due > 0 && <ExpBadge tone="due"><Clock className="h-3 w-3" />{summary.due} due soon</ExpBadge>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl mt-4 overflow-hidden">
        {filtered === null ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : !filtered.length ? (
          <div className="text-center py-16 text-sm text-slate-400">
            {items && items.length ? `No records match “${q}”.` : (
              <>No {meta.label.toLowerCase()} records yet. {can.has("org.edit") && <>Click <span className="font-medium text-slate-500">“Add {meta.singular}”</span> to create one.</>}</>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  {meta.cols.map(([k, l]) => <th key={k} className="px-4 py-2.5 font-semibold">{l}</th>)}
                  <th className="px-4 py-2.5 font-semibold">Expiries</th>
                  <th className="px-4 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60 transition-colors group" data-testid={`org-row-${r.id}`}>
                    {meta.cols.map(([k]) => (
                      <td key={k} className="px-4 py-3 text-slate-700 align-middle">
                        {k === "status" ? <StatusBadge value={r[k]} /> : (r[k] || <span className="text-slate-300">—</span>)}
                      </td>
                    ))}
                    <td className="px-4 py-3 align-middle">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r._expiries || {}).length === 0
                          ? <span className="text-slate-300 text-xs">—</span>
                          : Object.entries(r._expiries).map(([f, st]) => (
                            <ExpBadge key={f} tone={st.tone}>
                              {st.tone === "expired" ? <AlertTriangle className="h-3 w-3" /> : st.tone === "due" ? <Clock className="h-3 w-3" /> : null}
                              {expLabel(st)}
                            </ExpBadge>
                          ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap align-middle">
                      <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => open(r)} className="text-slate-400 hover:text-[#3b5b8c] p-1.5 rounded-md hover:bg-slate-100" title="Edit" data-testid={`org-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        {can.has("org.delete") && <button onClick={() => remove(r)} className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50" title="Delete" data-testid={`org-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-brand text-base flex items-center gap-2">
              <Icon className="h-4 w-4" style={{ color: ACCENT }} />
              {editing?.id ? "Edit" : "Add"} {meta.singular}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 pt-1">
            {meta.fields.map(([k, l, type]) => (
              <div key={k} className={type === "area" ? "sm:col-span-2 space-y-1" : "space-y-1"}>
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{l}</Label>
                {type === "area" ? (
                  <Textarea value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="text-sm" rows={2} data-testid={`org-field-${k}`} />
                ) : type === "status" && meta.statuses ? (
                  <Select value={form[k] || ""} onValueChange={(v) => setForm((f) => ({ ...f, [k]: v }))}>
                    <SelectTrigger className="h-9 text-sm bg-white" data-testid={`org-field-${k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{meta.statuses.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={type === "date" ? "date" : "text"} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="h-9 text-sm" data-testid={`org-field-${k}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: ACCENT }} onClick={save} disabled={saving} data-testid="org-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{editing?.id ? "Save changes" : `Create ${meta.singular}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
